import { motion } from "framer-motion";
import { Sparkles, Phone, Mail, ArrowDown } from "lucide-react";
import { PRICE_GROUPS } from "@/lib/prices";
import { STUDIO } from "@/lib/studio";
import { buildMailto } from "@/lib/request";
import { scrollToId, EASE } from "@/lib/scroll";
import { useGoTo } from "@/lib/useGoTo";
import { usePageMeta } from "@/lib/usePageMeta";
import { PriceCategory } from "@/components/site/price/PriceCategory";
import { ComboCard } from "@/components/site/price/ComboCard";
import { Packages } from "@/components/site/price/Packages";
import { GiftNote } from "@/components/site/GiftNote";
import { Reveal } from "@/components/site/Reveal";

const CATS = [...PRICE_GROUPS.map((g) => [g.id, g.title]), ["kombination", "Kombination"], ["pakete", "Monthly Packages"]];

const fade = (delay) => ({ initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.9, ease: EASE, delay } });

const Header = ({ onBook }) => (
  <header className="hero-glow pb-12 pt-28 sm:pt-36">
    <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 sm:px-8 lg:grid-cols-[1.2fr_0.8fr]">
    <div>
      <motion.p {...fade(0)} className="text-[11px] font-medium uppercase tracking-[0.32em] text-gold-deep">Maison Princess · Preisliste</motion.p>
      <h1 data-testid="price-title" className="mt-5 font-serif text-[2.6rem] font-medium leading-[1.02] text-ink sm:text-6xl lg:text-7xl">
        {["Preise", "& Rituale"].map((t, i) => (
          <span key={t} className="block overflow-hidden pb-[0.06em]">
            <motion.span className={`block ${i ? "italic text-gold-deep" : ""}`} initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1.1, ease: EASE, delay: 0.15 + i * 0.12 }}>
              {t}
            </motion.span>
          </span>
        ))}
      </h1>
      <motion.p {...fade(0.5)} className="mt-6 max-w-2xl text-[15px] leading-relaxed text-ink-soft sm:text-lg">
        Koreanische Gesichtsbehandlungen, Microneedling, Basic &amp; Special Treatments, Massagen sowie G8 Body Contouring und monatliche Beauty-Pakete – klar und übersichtlich. Jede Behandlung wird persönlich auf dich abgestimmt.
      </motion.p>
      <motion.div {...fade(0.65)} className="mt-6 inline-flex items-center gap-2 rounded-full border border-gold/40 bg-paper/80 px-4 py-2 text-sm text-ink" data-testid="price-consult-badge">
        <Sparkles className="h-4 w-4 text-gold-deep" strokeWidth={1.6} /> Individuelle Beratung bei allen Behandlungen inklusive
      </motion.div>
      <motion.div {...fade(0.8)} className="mt-8 flex flex-col gap-3 sm:flex-row">
        <button onClick={() => onBook("")} data-testid="price-header-book-btn" className="btn-gold inline-flex items-center justify-center gap-3 rounded-full px-8 py-4 text-[12px] font-semibold uppercase tracking-[0.24em]">
          Termin vereinbaren <ArrowDown className="h-4 w-4" />
        </button>
        <a href={STUDIO.phoneHref} data-testid="price-header-call-btn" className="inline-flex items-center justify-center gap-2 rounded-full border border-gold/50 px-8 py-4 text-[12px] font-semibold uppercase tracking-[0.22em] text-ink transition-colors duration-300 hover:bg-gold/10">
          <Phone className="h-4 w-4 text-gold-deep" /> Anrufen
        </a>
      </motion.div>
      <motion.div {...fade(0.95)} className="mt-8 max-w-2xl">
        <GiftNote />
      </motion.div>
    </div>
    <motion.div
      initial={{ clipPath: "inset(100% 0% 0% 0% round 999px 999px 0 0)" }}
      animate={{ clipPath: "inset(0% 0% 0% 0% round 999px 999px 0 0)" }}
      transition={{ duration: 1.4, ease: EASE, delay: 0.2 }}
      className="relative mx-auto h-[400px] w-full max-w-[380px] overflow-hidden rounded-t-full bg-champagne lg:h-[480px]"
    >
      <img src="/img/studio-facial.jpg" alt="Gesichtsbehandlung im Maison Princess" className="h-full w-full object-cover object-top" />
    </motion.div>
    </div>
  </header>
);

const CategoryNav = () => (
  <nav aria-label="Kategorien" className="sticky top-16 z-30 border-y border-gold/20 bg-cream/85 backdrop-blur-xl">
    <div className="mx-auto flex max-w-7xl gap-2 overflow-x-auto px-5 py-3 sm:px-8" data-lenis-prevent>
      {CATS.map(([id, label]) => (
        <button key={id} onClick={() => scrollToId(id)} data-testid={`price-tab-${id}`} className="shrink-0 rounded-full border border-gold/35 px-4 py-2 text-[12px] font-medium tracking-wide text-ink transition-colors duration-300 hover:border-gold hover:bg-gold/10">
          {label}
        </button>
      ))}
    </div>
  </nav>
);

const Closing = ({ onBook }) => (
  <Reveal className="mt-12 grid gap-8 border-t border-gold/25 pt-12 lg:grid-cols-2 lg:items-end">
    <div>
      <h2 className="font-serif text-4xl leading-tight text-ink sm:text-5xl">Bereit für <span className="italic text-gold-deep">deinen Moment?</span></h2>
      <ul className="mt-6 space-y-2 text-sm leading-relaxed text-ink-soft" data-testid="price-notes">
        <li>Alle Preise in Euro. „ab“-Preise richten sich nach der individuell gewählten Behandlung.</li>
        <li>Die Behandlungen sind kosmetische Pflege- und Wellnessanwendungen und ersetzen keine ärztliche Behandlung.</li>
        <li>Deine Anfrage ist noch keine verbindliche Terminbestätigung – ich melde mich persönlich bei dir.</li>
      </ul>
    </div>
    <div className="flex flex-col gap-3 sm:flex-row lg:justify-end">
      <button onClick={() => onBook("")} data-testid="price-closing-book-btn" className="btn-gold rounded-full px-8 py-4 text-[12px] font-semibold uppercase tracking-[0.22em]">Termin vereinbaren</button>
      <a href={buildMailto()} data-testid="price-closing-email-btn" className="inline-flex items-center justify-center gap-2 rounded-full border border-gold/50 px-8 py-4 text-[12px] font-semibold uppercase tracking-[0.22em] text-ink hover:bg-gold/10">
        <Mail className="h-4 w-4 text-gold-deep" /> E-Mail schreiben
      </a>
    </div>
  </Reveal>
);

export default function PriceList({ onSelect }) {
  usePageMeta(
    "Preisliste: Korean Facials, Massage & G8 | Maison Princess",
    "Preise für koreanische Gesichtsbehandlungen, Ayurveda- und Wellnessmassagen sowie G8 Body Contouring bei Maison Princess in Bensheim. Beratung inklusive.",
    "/preisliste",
  );
  const goTo = useGoTo();
  const onBook = (title) => {
    onSelect(title);
    goTo("termin");
  };

  return (
    <div data-testid="price-page">
      <Header onBook={onBook} />
      <CategoryNav />
      <div className="mx-auto max-w-7xl px-5 pb-24 sm:px-8">
        {PRICE_GROUPS.map((g) => (
          <PriceCategory key={g.id} g={g} onBook={onBook} />
        ))}
        <ComboCard onBook={onBook} />
        <Packages onBook={onBook} />
        <Closing onBook={onBook} />
      </div>
    </div>
  );
}
